const ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keydown',
    'touchstart',
    'touchmove',
    'touchend',
    'touchcancel',
    'pointerdown',
    'pointermove',
    'scroll',
] as const;

export const DEFAULT_ACTIVITY_PING_INTERVAL_MS = 60 * 1000;
export const DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS = 60 * 1000;

export const startActivityBasedPing = (
    pingIntervalMs: number,
    inactivityThresholdMs: number,
    onPing: () => void | Promise<void>,
    shouldKeepPinging?: () => boolean
): (() => void) => {
    let lastActivityAt: number | null = null;
    let intervalId: number | null = null;
    let keepAliveWatchId: number | null = null;

    const stopPingInterval = (): void => {
        if (intervalId !== null) {
            window.clearInterval(intervalId);
            intervalId = null;
        }
    };

    const stopKeepAliveWatch = (): void => {
        if (keepAliveWatchId !== null) {
            window.clearInterval(keepAliveWatchId);
            keepAliveWatchId = null;
        }
    };

    const isKeepAlive = (): boolean => shouldKeepPinging?.() === true;

    const startPingInterval = (): void => {
        if (intervalId !== null) {
            return;
        }

        intervalId = window.setInterval(async () => {
            if (!isKeepAlive()) {
                if (lastActivityAt === null) {
                    stopPingInterval();
                    return;
                }

                if (Date.now() - lastActivityAt >= inactivityThresholdMs) {
                    stopPingInterval();
                    return;
                }
            }

            await onPing();
        }, pingIntervalMs);
    };

    const markActivity = (): void => {
        const shouldStartInterval = intervalId === null;
        lastActivityAt = Date.now();

        if (shouldStartInterval) {
            startPingInterval();
        }
    };

    for (const eventName of ACTIVITY_EVENTS) {
        window.addEventListener(eventName, markActivity, { passive: true });
    }

    keepAliveWatchId = window.setInterval(() => {
        if (isKeepAlive() && intervalId === null) {
            startPingInterval();
        }
    }, pingIntervalMs);

    return (): void => {
        stopPingInterval();
        stopKeepAliveWatch();

        for (const eventName of ACTIVITY_EVENTS) {
            window.removeEventListener(eventName, markActivity);
        }
    };
};